import React, { useContext } from "react"
import { defineMessage, FormattedMessage, MessageDescriptor, useIntl } from "react-intl"
import { Link, Typography } from "@carrotfertility/carotene-core"
import { Link as RouterLink } from "react-router-dom"
import { useStateMachine } from "#/components/context/stateMachine/StateMachineV2"
import { useConvertTranslatedLabel } from "#/services/common-forms/useConvertTranslatedLabel"
import { FormSelectOption } from "@carrotfertility/carotene-core-x"
import { SelectWithTileStep } from "#/components/carrot-card-cost-share/steps/variants/SelectWithTileStep"
import { Paths } from "#/utils/Paths"
import { CarrotCardCostSharePreAuthContext } from "#/components/carrot-card-cost-share/state/CarrotCardCostSharePreAuthContext"
import { JourneySubTypeValues } from "#/components/carrot-card-cost-share/steps/shared/stepOptionValues"
import { CarrotRxRequiredBox } from "#/components/carrot-card-cost-share/steps/shared/CarrotRxRequiredBox"
import { useFlags } from "launchdarkly-react-client-sdk"

type WhatKindMenopauseOption = {
  label: MessageDescriptor
  value: string
}

const baseWhatKindMenopauseOptions: WhatKindMenopauseOption[] = [
  {
    label: defineMessage({ defaultMessage: "Medications" }),
    value: JourneySubTypeValues.MEDICATIONS
  },
  {
    label: defineMessage({ defaultMessage: "In-person care" }),
    value: JourneySubTypeValues.IN_PERSON_CARE
  },
  {
    label: defineMessage({ defaultMessage: "Online care" }),
    value: JourneySubTypeValues.ONLINE_CARE
  },
  {
    label: defineMessage({ defaultMessage: "Labs" }),
    value: JourneySubTypeValues.LABS
  },
  {
    label: defineMessage({ defaultMessage: "Nutrition counseling with a registered dietitian" }),
    value: JourneySubTypeValues.NUTRITION_COUNSELING
  },
  {
    label: defineMessage({ defaultMessage: "Temperature regulation products" }),
    value: JourneySubTypeValues.TEMP_REG_PRODUCTS
  }
]

const pelvicFloorTherapyOption: WhatKindMenopauseOption = {
  label: defineMessage({ defaultMessage: "Pelvic floor physical therapy" }),
  value: JourneySubTypeValues.PELVIC_FLOOR_THERAPY
}

export function WhatKindMenopauseStep(): JSX.Element {
  const intl = useIntl()
  const { send } = useStateMachine()
  const {
    dispatchFormState,
    formState: { journeySubType }
  } = useContext(CarrotCardCostSharePreAuthContext)
  const { pelvicFloorTherapyReimbursementLaunched } = useFlags()

  const whatKindMenopauseOptions = pelvicFloorTherapyReimbursementLaunched
    ? [...baseWhatKindMenopauseOptions.slice(0, 4), pelvicFloorTherapyOption, ...baseWhatKindMenopauseOptions.slice(4)]
    : baseWhatKindMenopauseOptions

  const formattedOptions = useConvertTranslatedLabel(whatKindMenopauseOptions) as FormSelectOption[]

  function onSubmit(formValues: { [key: string]: string }): void {
    dispatchFormState({ type: "UPDATE_FIELD", field: "journeySubType", value: formValues.journeySubType })
    const value = formValues.journeySubType
    if (value === JourneySubTypeValues.TEMP_REG_PRODUCTS || value === JourneySubTypeValues.PELVIC_FLOOR_THERAPY) {
      send(value)
    } else {
      send("")
    }
  }

  return (
    <SelectWithTileStep
      title={intl.formatMessage({ defaultMessage: "What kind of menopause expense is this?" })}
      description={
        <Typography sx={(theme) => ({ marginBlockEnd: theme.tokens.spacing.lg })}>
          <FormattedMessage
            defaultMessage="If you're not sure or don't see something listed, <link>contact us</link>."
            values={{
              link: (linkContent) => (
                <Link
                  component={RouterLink}
                  to={Paths.CARE_TEAM}
                  color="inherit"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {linkContent}
                </Link>
              )
            }}
          />
        </Typography>
      }
      selectName="journeySubType"
      options={formattedOptions}
      onSubmit={onSubmit}
      defaultValue={journeySubType}
    >
      <CarrotRxRequiredBox name="journeySubType" />
    </SelectWithTileStep>
  )
}
